/* ==========================================================================
   Messages -- the thread list and one masked conversation
   Numbers stay hidden unless both people choose to share. A price proposal is
   a structured message: accepting it makes it the record of the deal.
   ========================================================================== */

import { useEffect, useRef, useState } from 'react';
import type { OfferMessage, Role, Thread } from '../../types';
import { Icon } from '../../components/Icon';
import { Avatar, Card, Empty, Note } from '../../components/ui';
import { MessagePhotos, shrink as shrinkForChat } from '../../components/Photos';
import { api, photoUrl } from '../../api';
import { TranslateToggle, useTranslated } from '../../components/Translatable';
import { THREADS } from '../../data/seed';
import { byId, cleaner, house } from '../../lib/domain';
import { ago, fmtDate, isoDate, money } from '../../lib/time';
import { t } from '../../lib/i18n';
import {
  HOUSE_IDS, commit, markThreadRead, nav, openSheet, respondToOffer, sendMessage, state,
} from '../../store';

/* --------------------------------------------------------------- the list */

function ThreadList({ side }: { side: Role }) {
  const mine = THREADS.filter((x) =>
    side === 'cleaner' ? x.cleanerId === state.cleanerId : HOUSE_IDS.includes(x.houseId),
  );

  return (
    <div className="screen-pad">
      <Note>
        <Icon name="shield" /> {t('mg_numbershidden')}
      </Note>

      {mine.length === 0 && <Empty big={t('nothing')} sub={t('mg_noconv')} />}

      <div className="cardgrid">
      {mine.map((x) => {
        const other = side === 'cleaner' ? house(x.houseId) : cleaner(x.cleanerId);
        const otherName =
          side === 'cleaner' ? house(x.houseId).displayName : cleaner(x.cleanerId).name;
        const last = x.messages[x.messages.length - 1];
        const unread = side === 'cleaner' ? x.unreadForCleaner : x.unreadForHouse;
        const preview = !last
          ? ''
          : last.kind === 'offer'
            ? 'Price proposal: ' + money(last.rate) + t('perhour')
            : last.text;

        return (
          <Card
            key={x.id}
            onClick={() => nav(side === 'cleaner' ? 'c_chat' : 'h_chat', { id: x.id })}
          >
            <div className="row top">
              <Avatar p={other} />
              <div className="col grow">
                <div className="row between">
                  <b className="name">{otherName}</b>
                  <span className="sub tight">{last ? ago(last.at) : ''}</span>
                </div>
                <span
                  className="sub"
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                >
                  {preview}
                </span>
              </div>
              {unread > 0 && <span className="chip warm">{unread}</span>}
            </div>
          </Card>
        );
      })}
      </div>
    </div>
  );
}

export function CleanerMessages() { return <ThreadList side="cleaner" />; }
export function HouseMessages() { return <ThreadList side="house" />; }

/* --------------------------------------------------------------- the chat */

function Chat({ side }: { side: Role }) {
  const th = byId(THREADS, state.params.id!) as Thread;
  const [text, setText] = useState('');

  const [sharing, setSharing] = useState(false);
  const [phone, setPhone] = useState('');

  /* Pictures waiting to go with the next message. Homes only: a cleaner never
     sees the control, and the server refuses the ids regardless. */
  const canAttach = side === 'house';
  const [staged, setStaged] = useState<{ id: string }[]>([]);
  const [attaching, setAttaching] = useState(false);
  const photoInput = useRef<HTMLInputElement>(null);

  /* One toggle for the whole conversation, rather than a button per bubble. */
  const textIndexes = th.messages
    .map((m, i) => (m.kind === 'offer' ? -1 : i))
    .filter((i) => i >= 0);
  const tr = useTranslated(textIndexes.map((i) => (th.messages[i] as { text: string }).text));
  const shown = new Map(textIndexes.map((msgIndex, k) => [msgIndex, tr.view[k]]));

  /* Opening a conversation marks it read -- and so does a message arriving
     while it is open. The badge also clears here and now, rather than
     waiting for the next fetch: it used to sit at "2 unread" over the very
     messages being read, until the chat was closed and opened again. */
  useEffect(() => {
    if (th.unreadForHouse || th.unreadForCleaner) {
      if (side === 'house') th.unreadForHouse = 0; else th.unreadForCleaner = 0;
      commit();
    }
    void markThreadRead(th.id);
  }, [th.id, th.messages.length]);

  /* Sharing a number is just a message, and neither side is ever obliged
     to. */
  const shareNumber = () => {
    const cleaned = phone.trim();
    if (cleaned.length < 7) return;
    sendMessage(th.id, `You can reach me on ${cleaned}`);
    setPhone('');
    setSharing(false);
  };

  const submit = () => {
    if (!text.trim() && staged.length === 0) return;
    sendMessage(th.id, text, staged.map((p) => p.id));
    setText('');
    setStaged([]);
  };

  /* A photo is uploaded when it is picked, then sent with the next message.
     Uploading on send would mean the slow part happens after you have already
     said go, and a failure would look like a message that vanished. */
  const attach = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setAttaching(true);
    try {
      for (const file of Array.from(files).slice(0, 4)) {
        const shrunk = await shrinkForChat(file);
        const r = await api.addPhoto({
          kind: 'thread', ownerId: th.id, dataUrl: shrunk.dataUrl,
          width: shrunk.width, height: shrunk.height,
        });
        setStaged((now) => [...now, { id: r.id }]);
      }
    } catch {
      /* Quietly: the row of thumbnails simply does not grow, and the person
         can try again. A red banner over a chat for a photo that did not
         upload is worse than the missing thumbnail. */
    } finally {
      setAttaching(false);
      if (photoInput.current) photoInput.current.value = '';
    }
  };

  return (
    <div className="chatwrap">
      <div className="masknote">
        <Icon name="shield" /> {t('mg_private')}
      </div>

      <div className="chatlog">
        {th.messages.map((m, i) => {
          /* Times alone ran a message from seven weeks ago straight into one
             from today. A date line wherever the day changes. */
          const day = new Date(m.at).toDateString();
          const prev = i > 0 ? new Date(th.messages[i - 1].at).toDateString() : '';
          const line = day !== prev ? (
            <div className="hint" style={{ textAlign: 'center', margin: '6px 0' }} key={'d' + i}>
              {fmtDate(isoDate(new Date(m.at)), { long: true })}
            </div>
          ) : null;
          return [line, m.kind === 'offer' ? (
            <Offer key={i} m={m} i={i} side={side} threadId={th.id} />
          ) : (
            <div className={'bubble ' + (m.from === side ? 'me' : 'them')} key={i}>
              {shown.get(i) ?? m.text}
              <MessagePhotos ids={m.photoIds ?? []} />
              <span className="when">
                {new Date(m.at).getHours()}:
                {String(new Date(m.at).getMinutes()).padStart(2, '0')}
              </span>
            </div>
          )];
        })}
        <div className="row" style={{ justifyContent: 'center', gap: 8, marginTop: 6 }}>
          <button
            type="button"
            className="btn quiet sm"
            onClick={() => {
              /* Filled in from the number they signed up with -- Ozeret has
                 it, so asking them to type it out again was busywork. It
                 still waits for Send: a phone number given to a stranger
                 cannot be taken back, so one tap stands between the button
                 and the message. Editable, for anybody who would rather give
                 a different number. */
              setPhone(state.me?.phone ?? '');
              setSharing(true);
            }}
          >
            {t('mg_sharenumber')}
          </button>
          <TranslateToggle
            showing={tr.showing}
            busy={tr.busy}
            failed={tr.failed}
            changed={tr.changed}
            onToggle={tr.toggle}
          />
        </div>
      </div>

      {sharing && (
        <div className="chatbar" style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
          <div className="row" style={{ gap: 8 }}>
            <input
              className="input grow"
              type="tel"
              inputMode="tel"
              placeholder="845 555 0123"
              value={phone}
              autoFocus
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') shareNumber(); }}
            />
            <button type="button" className="btn" onClick={shareNumber}>Send number</button>
            <button
              type="button"
              className="btn quiet"
              onClick={() => { setSharing(false); setPhone(''); }}
            >
              {t('b_cancel')}
            </button>
          </div>
        </div>
      )}

      {/* What is about to be sent. Removing one here just drops it from the
          message; the photo stays on the conversation, which is where it was
          uploaded to. */}
      {staged.length > 0 && (
        <div className="chatbar staged">
          {staged.map((p) => (
            <button
              key={p.id}
              type="button"
              className="thumb"
              aria-label={t('mg_removepic')}
              onClick={() => setStaged((now) => now.filter((x) => x.id !== p.id))}
            >
              <img src={photoUrl(p.id)} alt="" />
            </button>
          ))}
          <span className="hint">{t('mg_tapremove')}</span>
        </div>
      )}

      <div className="chatbar">
        <button
          type="button"
          className="btn ghost"
          style={{ padding: '11px 13px', flex: 'none' }}
          title={t('b_negotiate')}
          aria-label={t('b_negotiate')}
          onClick={() => openSheet({ name: 'negotiate', id: th.id, rate: null })}
        >
          <Icon name="dollar" />
          <span className="pricelabel">Price</span>
        </button>

        {/* Only a home. A cleaner has no picture control anywhere in the app
            except their own profile photo -- Oster's rule, and the reason
            this feature is small enough to be safe. */}
        {canAttach && (
          <>
            <button
              type="button"
              className="btn ghost"
              style={{ padding: '11px 13px', flex: 'none' }}
              title={t('mg_sendpic')}
              aria-label={t('mg_sendpic')}
              disabled={attaching}
              onClick={() => photoInput.current?.click()}
            >
              <Icon name="camera" />
            </button>
            <input
              ref={photoInput}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={(e) => void attach(e.target.files)}
            />
          </>
        )}
        <input
          className="input grow"
          placeholder={t('mg_write')}
          autoComplete="off"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
        />
        <button
          type="button"
          className="btn"
          style={{ padding: '11px 14px' }}
          onClick={submit}
          aria-label={t('b_send')}
        >
          <Icon name="send" />
        </button>
      </div>
    </div>
  );
}

function Offer({
  m, i, side, threadId,
}: { m: OfferMessage; i: number; side: Role; threadId: string }) {
  const mine = m.from === side;
  return (
    <div className={'offer ' + m.state}>
      <span className="hd">{mine ? 'You proposed' : 'They proposed'}</span>
      <div className="row between">
        <span className="big">
          {money(m.rate)}
          <small style={{ fontSize: 12, color: 'var(--muted)' }}>{t('perhour')}</small>
        </span>
        <span className="sub">
          {m.hours} {t('hours')} · {money(m.rate * m.hours)} {t('total')}
        </span>
      </div>
      {m.note && <span className="sub">&quot;{m.note}&quot;</span>}

      {m.state === 'open' && !mine ? (
        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn sm grow"
            onClick={() => respondToOffer(threadId, i, true)}
          >
            {t('b_accept')}
          </button>
          <button
            type="button"
            className="btn quiet sm"
            onClick={() => respondToOffer(threadId, i, false)}
          >
            {t('b_decline')}
          </button>
        </div>
      ) : (
        <span
          className={
            'chip' + (m.state === 'accepted' ? ' good' : m.state === 'declined' ? ' bad' : '')
          }
        >
          {m.state === 'accepted'
            ? 'Agreed — this is the record'
            : m.state === 'declined'
              ? 'Declined'
              : 'Waiting for a reply'}
        </span>
      )}
    </div>
  );
}

export function CleanerChat() { return <Chat side="cleaner" />; }
export function HouseChat() { return <Chat side="house" />; }
